import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { FButton } from "./FButton";

type Row = {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
};

export function FollowingSheet({
  userId,
  open,
  onClose,
}: {
  userId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [followingMap, setFollowingMap] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("follows")
        .select("following:profiles!following_id(id, username, display_name, avatar_url)")
        .eq("follower_id", userId);
      if (cancelled) return;
      const list = ((data ?? []) as any[])
        .map((r) => r.following)
        .filter(Boolean) as Row[];
      setRows(list);

      if (user && list.length > 0) {
        const ids = list.map((r) => r.id);
        const { data: mine } = await supabase
          .from("follows")
          .select("following_id")
          .eq("follower_id", user.id)
          .in("following_id", ids);
        const map: Record<string, boolean> = {};
        for (const m of mine ?? []) map[(m as any).following_id] = true;
        setFollowingMap(map);
      } else {
        setFollowingMap({});
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId, user]);

  const toggle = async (r: Row) => {
    if (!user) return;
    const isFollowing = !!followingMap[r.id];
    setFollowingMap((m) => ({ ...m, [r.id]: !isFollowing }));
    if (isFollowing) {
      const { error } = await supabase
        .from("follows")
        .delete()
        .eq("follower_id", user.id)
        .eq("following_id", r.id);
      if (error) {
        setFollowingMap((m) => ({ ...m, [r.id]: true }));
        toast.error(error.message);
      }
    } else {
      const { error } = await supabase
        .from("follows")
        .insert({ follower_id: user.id, following_id: r.id });
      if (error) {
        setFollowingMap((m) => ({ ...m, [r.id]: false }));
        toast.error(error.message);
      }
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="bottom"
        className="h-[80vh] rounded-t-[var(--radius-lg)] p-0 flex flex-col"
      >
        <SheetHeader className="px-5 pt-5 pb-3">
          <SheetTitle>Following</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-3 pb-6">
          {loading ? (
            <ul className="space-y-2 px-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <li
                  key={i}
                  className="h-14 rounded-[var(--radius-md)] animate-pulse"
                  style={{ backgroundColor: "var(--colour-surface-raised)" }}
                />
              ))}
            </ul>
          ) : rows.length === 0 ? (
            <p className="text-center text-[14px] text-[var(--colour-text-secondary)] mt-8">
              Not following anyone yet.
            </p>
          ) : (
            <ul className="space-y-1">
              {rows.map((r) => {
                const isSelf = user?.id === r.id;
                const isFollowing = !!followingMap[r.id];
                return (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 px-2 py-2 rounded-[var(--radius-md)]"
                  >
                    <Link
                      to={`/u/${r.username}`}
                      onClick={onClose}
                      className="flex items-center gap-3 flex-1 min-w-0"
                    >
                      {r.avatar_url ? (
                        <img
                          src={r.avatar_url}
                          alt={r.display_name}
                          className="w-10 h-10 rounded-[var(--radius-full)] object-cover shrink-0"
                        />
                      ) : (
                        <div
                          className="w-10 h-10 rounded-[var(--radius-full)] shrink-0 flex items-center justify-center text-[14px] font-semibold"
                          style={{
                            backgroundColor: "var(--colour-surface-raised)",
                            color: "var(--colour-text-secondary)",
                          }}
                        >
                          {r.display_name?.slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[14px] font-semibold truncate">
                          {r.display_name}
                        </div>
                        <div className="text-[12px] text-[var(--colour-text-secondary)] truncate">
                          @{r.username}
                        </div>
                      </div>
                    </Link>
                    {!isSelf && user && (
                      <FButton
                        variant={isFollowing ? "outline" : "primary"}
                        onClick={() => toggle(r)}
                        className="px-4 h-9 text-[13px] shrink-0"
                      >
                        {isFollowing ? "Following" : "Follow"}
                      </FButton>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
